import { Autocomplete, Paper, TextField } from '@mui/material';
import type { IngestedEventSample } from '@weft/types';
import { withDefault } from '@weft/utils';
import { useLatestEvents } from 'apps/app/src/hooks/requests/use-latest-events';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { VyRangePicker } from '../../../components/new-design/inputs/VyRangePicker';
import { withPageLayout } from '../../../components/new-design/layout/VyPageLayout';
import { VyTableToolbar } from '../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { useAllCustomers } from '../../../hooks/requests/use-customers';
import { useMeters } from '../../../hooks/requests/use-meters';
import { useFromDayParam } from '../../../hooks/search-params/use-from-day-param';
import { usePageParam } from '../../../hooks/search-params/use-page-param';
import { usePageSizeParam } from '../../../hooks/search-params/use-page-size-param';
import { useToDayParam } from '../../../hooks/search-params/use-to-day-param';
import { usePaginationResetHandler } from '../../../hooks/use-pagination-reset-handler';
import { useStateParams } from '../../../hooks/use-state-params';
import { deserializeStringArrayParam, serializeStringArrayParam } from '../../../utils/params-formatting';
import { EventDataDrawer } from './events-page-components/DataEventDrawer';
import { EventsTable } from './events-page-components/EventsTable/EventsTable';

const EventsPage: React.FC = () => {
  const [page] = usePageParam();
  const [pageSize] = usePageSizeParam();

  const [paramsDayFrom] = useFromDayParam();
  const [paramsDayTo] = useToDayParam();

  const startTime = paramsDayFrom?.utc(true).toDate();
  const endTime = paramsDayTo?.utc(true).toDate();

  const { data: { customers } } = useAllCustomers();
  const { data: { meters } } = useMeters();

  const [selectedCustomersIds, setCustomersIds] = useStateParams<string[]>(
    [],
    'customers',
    serializeStringArrayParam,
    deserializeStringArrayParam,
  );
  const [selectedMetersIds, setSelectedMetersIds] = useStateParams<string[]>(
    [],
    'meters',
    serializeStringArrayParam,
    deserializeStringArrayParam,
  );

  const { data: latestEventsResponse, isFetching: isFetchingEvents } = useLatestEvents({
    page: withDefault(page, 0),
    pageSize,
    startTime,
    endTime,
    metersIds: JSON.stringify(selectedMetersIds ?? []),
    customersIds: JSON.stringify(selectedCustomersIds ?? []),
  });
  const { isDialogOpen: isDataDrawerOpen, handleOpen: openDataDrawer, handleClose: closeDataDrawer } = useDialog();
  const [selectedEvent, setSelectedEvent] = useState<IngestedEventSample | undefined>(undefined);

  const events = latestEventsResponse?.events ?? [];

  usePaginationResetHandler(withDefault(latestEventsResponse?.countedFilteredEvents, 0));

  const handleSelectEvent = (event: IngestedEventSample) => {
    setSelectedEvent(event);
    openDataDrawer();
  };

  return (
    <>
      <Helmet>
        <title>Events</title>
      </Helmet>

      <Paper
        variant="outlined"
        sx={theme => ({
          borderRadius: '12px',
          boxShadow: theme.shadows[1],
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        })}
      >
        <EventDataDrawer 
          dataDrawerOpen={isDataDrawerOpen}
          closeDataDrawer={closeDataDrawer}
          event={selectedEvent}
        />

        <VyTableToolbar padding="24px 16px">
          <VyTableToolbar.Queries
            sx={{
              alignItems: 'start',
            }}
          >
            <VyRangePicker />

            <div>
              <Autocomplete
                multiple
                size="small"
                limitTags={1}
                options={customers}
                getOptionLabel={(customer) => customer.name}
                onChange={(_, value) =>
                  setCustomersIds(value.map((customer) => customer.id))
                }
                value={customers.filter((customer) =>
                  withDefault(selectedCustomersIds, []).includes(customer.id),
                )}
                sx={{
                  width: '240px',
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customers"
                    fullWidth
                  />
                )}
                disableClearable
              />
            </div>

            <Autocomplete
              multiple
              size="small"
              limitTags={1}
              options={meters}
              getOptionLabel={(meterId) => meterId.name}
              onChange={(_, value) =>
                setSelectedMetersIds(value.map((meter) => meter.id))
              }
              value={meters.filter((meter) =>
                withDefault(selectedMetersIds, []).includes(meter.id),
              )}
              sx={{
                width: '240px',
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Products"
                  fullWidth
                />
              )}
              disableClearable
            />
          </VyTableToolbar.Queries>
        </VyTableToolbar>

        <EventsTable
          items={events}
          loadingItems={isFetchingEvents}
          itemsCount={withDefault(latestEventsResponse?.countedFilteredEvents, 0)}
          onRowClick={(row) => handleSelectEvent(row.row as IngestedEventSample)}
        />
      </Paper>
    </>
  );
};

const EventsPageWithSuspenseBoundary = withPageLayout(withGlobalSearchParams(EventsPage), {
  path: ['Data Metering', 'Events'],
  title: 'Events',
});
export { EventsPageWithSuspenseBoundary as EventsPage };
